import { ProfileGenTestCaseViewParsed, TEST_CASE_DESCRIPTION_LABEL_LOOKUP } from "redux/slices/lensEditor";
import PCheckbox from "components/PCheckbox";
import { Chip, Divider, Table, TableBody, TableCell, TableRow, Tooltip } from "@mui/material";
import { isMultiResponse, useTestJsonParser } from "./useTestJsonParser";
import { Fragment, useState } from "react";
import theme from "theme";
import { Cancel, CheckCircle } from "@mui/icons-material";
import ProgressBar from "components/ProgressBar";
import { TestCaseCellValue } from "generated/graphql";
import displayCellValue from "components/FormField/displayCellValue";
import { isNoneCellOrNullish } from "Backend";
import { stripNull } from "helpers/coll";
import useTestCaseManager from "./useTestCaseManager";

const CellValueTestResult = (props: { testResult: TestCaseCellValue }) => {
  if (props.testResult.issue) {
    return <p className="text-xs">{props.testResult.issue}</p>;
  } else {
    return <p className="text-xs">{displayCellValue(props.testResult.cellValue ?? undefined)}</p>;
  }
};
const TestCaseResult = ({ testCase }: { testCase: ProfileGenTestCaseViewParsed }) => {
  const [showSuccesses, setShowSuccesses] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const [showFullJson, setShowFullJson] = useState(false);
  const jsonParser = useTestJsonParser();
  const { updateTestCase } = useTestCaseManager();
  const result = stripNull(testCase.lastResult);

  const handleUpdateTestExpectation = async (evt: React.MouseEvent<HTMLAnchorElement>) => {
    evt.preventDefault();

    const parsed = jsonParser(testCase.expectedResponse);

    if (parsed === undefined || result === undefined) return;

    if (isMultiResponse(parsed)) {
      // replace the correct item for multi-response tests
      const withNewExpectation = {
        ...parsed,
        items: parsed.items.map((_: Record<string, unknown>, i: number) => {
          return JSON.parse(result.responseResults[i]?.actualJson || "");
        }),
      };
      updateTestCase({ ...testCase, expectedResponse: JSON.stringify(withNewExpectation) });
    } else {
      // For single-response tests, we can use the response as-is
      updateTestCase({ ...testCase, expectedResponse: result.responseResults[0]?.actualJson || "" });
    }
  };

  if (!result) return null;

  return (
    <div className="flex flex-col p-0.5">
      {result.executionFailure ? (
        <div className="flex items-center gap-2">
          <Cancel
            sx={{
              color: theme.palette.error.main,
            }}
          />
          <p className="text-xs">Failure: {result.executionFailure}</p>
        </div>
      ) : (
        result.responseResults.map((responseResult, i) => (
          <div key={i}>
            <div className="flex items-center">
              <h1 className="font-semibold text-md">
                Response {i + 1} {responseResult.actualJson === "{}" && " (unmatched)"}
              </h1>
            </div>
            <div className="flex items-center gap-2 justify-between pb-1">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  {responseResult.passed ? (
                    <Tooltip title="Success">
                      <CheckCircle
                        sx={{
                          color: "green",
                        }}
                      />
                    </Tooltip>
                  ) : (
                    <Cancel sx={{ color: theme.palette.error.main }} />
                  )}
                  <Tooltip title={`${responseResult.score * 100}%`} placement="top">
                    <p
                      className="text-lg font-semibold"
                      style={{
                        color: responseResult.passed ? theme.palette.success.main : theme.palette.error.main,
                      }}>
                      {(responseResult.score * 100).toFixed(1)}%
                    </p>
                  </Tooltip>

                  {testCase.expectFailure && <p className="text-xs font-semibold">*Expects Failure</p>}
                </div>
                {testCase.labels.length > 0 && (
                  <div className="flex gap-2">
                    {testCase.labels.map((label) => (
                      <Chip
                        size="small"
                        color="primary"
                        variant="outlined"
                        key={label}
                        label={TEST_CASE_DESCRIPTION_LABEL_LOOKUP[label] ?? `Unknown Label - ${label}`}
                      />
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center">
                <PCheckbox
                  label="Show successes"
                  checked={showSuccesses}
                  onChange={() => setShowSuccesses(!showSuccesses)}
                />
                <PCheckbox label="Show raw" checked={showRaw} onChange={() => setShowRaw(!showRaw)} />
                <div className="flex justify-between">
                  <Tooltip title="Replace current test expectations with these results">
                    <a onClick={handleUpdateTestExpectation} className="pattern-link">
                      Update expectation from result
                    </a>
                  </Tooltip>
                </div>
              </div>
            </div>
            <Divider />
            <Table>
              <TableRow>
                <TableCell></TableCell>
                <TableCell>
                  <p className="font-semibold">Score</p>
                </TableCell>
                <TableCell>
                  <p className="font-semibold">Expected</p>
                </TableCell>
                <TableCell>
                  <p className="font-semibold">Actual</p>
                </TableCell>
                <TableCell>
                  <p className="font-semibold">Reason</p>
                </TableCell>
              </TableRow>
              <TableBody>
                {responseResult.cellResults.map((cellResult) => {
                  const isPass = cellResult.passed;
                  const isFail = !isPass;
                  const isNull = isNoneCellOrNullish(cellResult.actualCellValue.cellValue);

                  // If the actual value is null, a missing raw result is fine
                  const isRawPass =
                    (isPass && isNull) ||
                    (cellResult.rawResult?.similarity ?? 0) > (cellResult.rawResult?.minPassingScore ?? 1);
                  const isRawFail = !isRawPass;

                  const valueRow = ((showSuccesses && isPass) || isFail) && (
                    <TableRow>
                      <TableCell>
                        <p className="font-semibold">{cellResult.cellName}</p>
                      </TableCell>
                      <TableCell>
                        <ProgressBar size="small" showBar={false} value={cellResult.cellValueScore * 100} />
                      </TableCell>
                      <TableCell>
                        <CellValueTestResult testResult={cellResult.expectedCellValue} />
                      </TableCell>
                      <TableCell>
                        <CellValueTestResult testResult={cellResult.actualCellValue} />
                      </TableCell>
                      <TableCell>{cellResult.cellValueScoreReason}</TableCell>
                    </TableRow>
                  );
                  const rawValueRow = showRaw && ((showSuccesses && isRawPass) || isRawFail) && (
                    <TableRow>
                      <TableCell>{cellResult.cellName} Raw</TableCell>
                      <TableCell>
                        <ProgressBar size="small" showBar={false} value={cellResult.cellValueScore * 100} />{" "}
                      </TableCell>
                      <TableCell>{cellResult.rawResult?.expected}</TableCell>
                      <TableCell>{cellResult.rawResult?.actual}</TableCell>
                    </TableRow>
                  );

                  return (
                    <Fragment key={cellResult.cellId.toString()}>
                      {valueRow}
                      {rawValueRow}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
            <Divider />
            <PCheckbox label="Show JSON" checked={showFullJson} onChange={() => setShowFullJson(!showFullJson)} />
            {showFullJson && (
              <div className="grid grid-cols-2 gap-2 w-full overflow-hidden mb-2">
                <div className="w-full overflow-hidden">
                  <p className="font-semibold">Expected</p>
                  <p className="font-mono whitespace-pre-wrap">
                    {JSON.stringify(JSON.parse(responseResult.expectedJson), null, 2)}
                  </p>
                </div>
                <div className="w-full overflow-hidden">
                  <p className="font-semibold">Actual</p>
                  <p className="font-mono whitespace-pre-wrap">
                    {JSON.stringify(JSON.parse(responseResult.actualJson), null, 2)}
                  </p>
                </div>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
};

export default TestCaseResult;
